import React, { useState } from 'react';
import { Search, Upload, Check, X, Edit3, Save } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { Product, products as initialProducts } from '../../data/products';
export function AdminProducts() {
  const {
    products,
    updateProducts
  } = useStore();
  const [search, setSearch] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editForm, setEditForm] = useState<Partial<Product>>({});
  const filteredProducts = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.sku && p.sku.toLowerCase().includes(search.toLowerCase()));
  const handleImport = () => {
    setImportError('');
    setImportSuccess('');
    try {
      const parsed = JSON.parse(importText);
      if (!Array.isArray(parsed)) {
        throw new Error('Import data must be a JSON array of products.');
      }
      // Basic validation
      const validProducts = parsed.filter(p => p.id && p.name && p.price !== undefined);
      if (validProducts.length === 0) {
        throw new Error('No valid products found in JSON. Ensure id, name, and price exist.');
      }
      // Merge by SKU or ID
      const newProducts = [...products];
      let updated = 0;
      let added = 0;
      validProducts.forEach(vp => {
        const existingIndex = newProducts.findIndex(p => vp.sku && p.sku === vp.sku || p.id === vp.id);
        if (existingIndex >= 0) {
          newProducts[existingIndex] = {
            ...newProducts[existingIndex],
            ...vp
          };
          updated++;
        } else {
          newProducts.push(vp as Product);
          added++;
        }
      });
      updateProducts(newProducts);
      setImportSuccess(`Successfully imported ${added} new products and updated ${updated} existing products.`);
      setImportText('');
      setTimeout(() => {
        setShowImport(false);
        setImportSuccess('');
      }, 3000);
    } catch (e: any) {
      setImportError(e.message || 'Failed to parse JSON.');
    }
  };
  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset products to the original default catalog?')) {
      updateProducts(initialProducts);
      setImportSuccess('Products reset to default.');
      setTimeout(() => setImportSuccess(''), 3000);
    }
  };
  const handleEditClick = (product: Product) => {
    setEditingProduct(product);
    setEditForm(product);
  };
  const handleSaveEdit = () => {
    if (editingProduct && editForm) {
      const newProducts = products.map(p => p.id === editingProduct.id ? {
        ...p,
        ...editForm
      } as Product : p);
      updateProducts(newProducts);
      setEditingProduct(null);
      setImportSuccess('Product updated successfully.');
      setTimeout(() => setImportSuccess(''), 3000);
    }
  };
  return <div className="space-y-6" data-id="element-742">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-743">
        <h1 className="text-2xl font-extrabold text-gray-900" data-id="element-744">Products</h1>
        <div className="flex items-center gap-3" data-id="element-745">
          <div className="relative" data-id="element-746">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-747" />
            <input type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-yarbo-dark focus:border-transparent w-full sm:w-64" data-id="element-748" />
          </div>
          <button onClick={() => setShowImport(true)} className="flex items-center gap-2 px-4 py-2 bg-yarbo-dark text-white rounded-lg text-sm font-bold hover:bg-black transition-colors" data-id="element-749">
            <Upload className="w-4 h-4" data-id="element-750" /> Import
          </button>
        </div>
      </div>

      {importSuccess && !showImport && <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm font-medium" data-id="element-751">
          {importSuccess}
        </div>}

      {showImport && <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-752">
          <div className="flex justify-between items-center mb-4" data-id="element-753">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-754">Import Products</h2>
            <button onClick={() => setShowImport(false)} className="text-gray-400 hover:text-gray-600" data-id="element-755">
              Close
            </button>
          </div>

          <textarea value={importText} onChange={e => setImportText(e.target.value)} placeholder="Paste JSON array of products here..." className="w-full h-48 border-gray-300 rounded-lg shadow-sm focus:border-yarbo-dark focus:ring-yarbo-dark font-mono text-sm mb-4" data-id="element-756" />
          {importError && <p className="text-red-600 text-sm mb-4" data-id="element-757">{importError}</p>}
          {importSuccess && <p className="text-green-600 text-sm mb-4" data-id="element-758">{importSuccess}</p>}
          <div className="flex justify-between items-center" data-id="element-759">
            <div className="flex gap-3" data-id="element-760">
              <button onClick={handleImport} className="bg-yarbo-dark text-white font-bold px-4 py-2 rounded-lg hover:bg-black" data-id="element-761">
                Import JSON
              </button>
              <button onClick={() => setShowImport(false)} className="bg-white border border-gray-300 text-gray-700 font-bold px-4 py-2 rounded-lg hover:bg-gray-50" data-id="element-762">
                Cancel
              </button>
            </div>
            <button onClick={handleReset} className="text-red-600 hover:underline text-sm font-bold" data-id="element-763">
              Reset to Default
            </button>
          </div>
        </div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-764">
        <div className="overflow-x-auto" data-id="element-765">
          <table className="w-full text-left text-sm whitespace-nowrap" data-id="element-766">
            <thead className="bg-gray-50 text-gray-500 font-medium" data-id="element-767">
              <tr data-id="element-768">
                <th className="px-6 py-4 w-16" data-id="element-769">Image</th>
                <th className="px-6 py-4" data-id="element-770">Product</th>
                <th className="px-6 py-4" data-id="element-771">SKU</th>
                <th className="px-6 py-4" data-id="element-772">Price</th>
                <th className="px-6 py-4" data-id="element-773">Group</th>
                <th className="px-6 py-4" data-id="element-774">YarboCare</th>
                <th className="px-6 py-4" data-id="element-775"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-776">
              {filteredProducts.map(product => <tr key={product.id} className="hover:bg-gray-50" data-id="element-777">
                  <td className="px-6 py-4" data-id="element-778">
                    <div className="w-10 h-10 bg-gray-50 rounded border border-gray-200 p-1" data-id="element-779">
                      <img src={product.image || product.imageUrl} alt="" className="w-full h-full object-contain" data-id="element-780" />
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-781">
                    <div className="font-bold text-gray-900" data-id="element-782">
                      {product.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-[200px]" data-id="element-783">
                      {product.publicCategories?.join(', ')}
                    </div>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-gray-600" data-id="element-784">
                    {product.sku}
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900" data-id="element-785">
                    ${product.price.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-gray-600" data-id="element-786">
                    {product.primaryGroup}
                  </td>
                  <td className="px-6 py-4" data-id="element-787">
                    {product.eligibleForYarboCare ? <Check className="w-4 h-4 text-green-500" data-id="element-788" /> : <span className="text-gray-300" data-id="element-789">-</span>}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-790">
                    <button onClick={() => handleEditClick(product)} className="text-yarbo-dark font-bold hover:underline flex items-center gap-1 justify-end w-full" data-id="element-791">
                      <Edit3 className="w-4 h-4" data-id="element-792" /> Edit
                    </button>
                  </td>
                </tr>)}
              {filteredProducts.length === 0 && <tr data-id="element-793">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-794">
                    No products found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Product Modal */}
      {editingProduct && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-gray-900/50 backdrop-blur-sm" data-id="element-795">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" data-id="element-796">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-797">
              <h2 className="text-xl font-extrabold text-gray-900" data-id="element-798">
                Edit Product
              </h2>
              <button onClick={() => setEditingProduct(null)} className="text-gray-400 hover:text-gray-600" data-id="element-799">
                <X className="w-6 h-6" data-id="element-800" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-id="element-801">
              <div data-id="element-802">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-803">
                  Name
                </label>
                <input type="text" value={editForm.name || ''} onChange={e => setEditForm({
              ...editForm,
              name: e.target.value
            })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yarbo-dark focus:border-transparent" data-id="element-804" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-805">
                <div data-id="element-806">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-807">
                    SKU
                  </label>
                  <input type="text" value={editForm.sku || ''} onChange={e => setEditForm({
                ...editForm,
                sku: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yarbo-dark focus:border-transparent" data-id="element-808" />
                </div>
                <div data-id="element-809">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-810">
                    Price ($)
                  </label>
                  <input type="number" value={editForm.price || 0} onChange={e => setEditForm({
                ...editForm,
                price: parseFloat(e.target.value)
              })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yarbo-dark focus:border-transparent" data-id="element-811" />
                </div>
              </div>

              <div data-id="element-812">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-813">
                  Image URL
                </label>
                <input type="text" value={editForm.imageUrl || editForm.image || ''} onChange={e => setEditForm({
              ...editForm,
              imageUrl: e.target.value,
              image: e.target.value
            })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yarbo-dark focus:border-transparent" data-id="element-814" />
              </div>

              <div data-id="element-815">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-816">
                  Short Description
                </label>
                <textarea value={editForm.shortDescription || ''} onChange={e => setEditForm({
              ...editForm,
              shortDescription: e.target.value
            })} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yarbo-dark focus:border-transparent" data-id="element-817" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-818">
                <div data-id="element-819">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-820">
                    Primary Group
                  </label>
                  <input type="text" value={editForm.primaryGroup || ''} onChange={e => setEditForm({
                ...editForm,
                primaryGroup: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yarbo-dark focus:border-transparent" data-id="element-821" />
                </div>
                <div data-id="element-822">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-823">
                    Category
                  </label>
                  <input type="text" value={editForm.category || ''} onChange={e => setEditForm({
                ...editForm,
                category: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yarbo-dark focus:border-transparent" data-id="element-824" />
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4" data-id="element-825">
                <input type="checkbox" id="eligibleForYarboCare" checked={editForm.eligibleForYarboCare || false} onChange={e => setEditForm({
              ...editForm,
              eligibleForYarboCare: e.target.checked
            })} className="w-4 h-4 text-yarbo-dark rounded focus:ring-yarbo-dark" data-id="element-826" />
                <label htmlFor="eligibleForYarboCare" className="text-sm font-medium text-gray-700" data-id="element-827">
                  Eligible for YarboCare
                </label>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3" data-id="element-828">
              <button onClick={() => setEditingProduct(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100" data-id="element-829">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-yarbo-dark text-white rounded-lg font-bold hover:bg-black flex items-center gap-2" data-id="element-830">
                <Save className="w-4 h-4" data-id="element-831" /> Save Changes
              </button>
            </div>
          </div>
        </div>}
    </div>;
}